import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Box } from "@mui/material";
import { enReportServiceType } from "./helper";
import React, { useEffect, useRef, useState, useMemo } from "react";
import TextInputField from "../common/TextInputField";
import ImageUpload from "../common/ImageUpload";
import ScrollableWrapper from "../common/ScrollableWrapper";
import ServiceBenefitsWidget from "./ServiceBenefitsWidget";
import ActiveIngredientsWidget from "./ActiveIngredientsWidget";
import OptionalFieldSection from "./OptionalFieldSection";
import AttributeRenderer from "../common/AttributeRenderer";
import { FIELD_CONFIG } from "./fieldConfig";
import { ServiceDetails } from "./types";
import { validateDurationAttribute } from "./NumberAndRangeElement";
import { isDuplicateName } from "../../utils/duplicateName";

const DUPLICATE_NAME_ERROR = "A service with this name already exists";
type ExistingService = { id?: string | null; name?: string | null };

type Props = {
  serviceDetails: ServiceDetails;
  setServiceDetails: (
    value: ServiceDetails | ((prev: ServiceDetails) => ServiceDetails)
  ) => void;
  onNameEditAttempt?: (onConfirm: () => void, onCancel: () => void) => void;
  onImageSelect?: (file: File | null) => void;
};

const ServiceBuilderStep2 = ({
  serviceDetails,
  setServiceDetails,
  onNameEditAttempt,
  onImageSelect,
}: Props) => {
  const theme: Theme = useTheme();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [isEditingName, setIsEditingName] = useState(false);

  // Memoized service type calculation
  const serviceType = useMemo(() => {
    return serviceDetails?.serviceType
      ? enReportServiceType[serviceDetails.serviceType]
      : null;
  }, [serviceDetails?.serviceType]);

  // Memoized dynamic fields based on service type
  const dynamicFields = useMemo(() => {
    if (!serviceType) return null;

    const filteredFields = FIELD_CONFIG.filter((field) =>
      field.serviceTypes.includes(serviceType)
    );

    return filteredFields.map((field, index) => (
      <OptionalFieldSection
        key={`${field.key}-${field.title}-${index}`} // Handle duplicate keys
        title={field.title}
        placeholder={field.placeholder}
        value={(serviceDetails as any)?.[field.key] || ""}
        onChange={(e) =>
          setServiceDetails((prev: any) => ({
            ...prev,
            [field.key]: e.target.value,
          }))
        }
        type={field.type}
        options={field.options}
      />
    ));
  }, [serviceType, serviceDetails, setServiceDetails]);
  const [uploadedImage, setUploadedImage] = useState<string | null>(
    serviceDetails.imageURL || null
  );

  // Initialize description as empty string on component mount
  useEffect(() => {
    if (serviceDetails?.name) {
      setIsEditingName(false);
    }
  }, [serviceDetails]);

  // Check and truncate description if it exceeds 500 characters on initial render
  useEffect(() => {
    const description = serviceDetails?.description;
    if (description && description.length > 500) {
      setServiceDetails((prev: any) => ({
        ...prev,
        description: description.slice(0, 500),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNameChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;

    if (!isEditingName && onNameEditAttempt) {
      // First time editing, trigger confirmation
      onNameEditAttempt?.(
        () => {
          // Confirm: allow the edit
          setIsEditingName(true);
          setServiceDetails((prev: any) => ({ ...prev, name: newValue }));

          // Restore cursor position after state update
          setTimeout(() => {
            if (nameInputRef.current) {
              nameInputRef.current.focus();
              nameInputRef.current.setSelectionRange(
                cursorPosition,
                cursorPosition
              );
            }
          }, 0);
        },
        () => {
          // Cancel: keep current value (do nothing)
        }
      );
    } else {
      // Already confirmed or no confirmation needed
      setServiceDetails((prev: any) => ({ ...prev, name: newValue }));

      // Preserve cursor position after React re-render
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.setSelectionRange(
            cursorPosition,
            cursorPosition
          );
        }
      }, 0);
    }
  };

  return (
    <Box sx={styles.container}>
      <ScrollableWrapper ref={scrollableRef}>
        <Box sx={styles.content}>
          <TextInputField
            label="Name"
            value={serviceDetails?.name || ""}
            handleChange={handleNameChange}
            name="textInput"
            inputRef={nameInputRef}
          />
          <Box>
            <Box sx={styles.title(theme)}>Description</Box>
            <TextInputField
              // label="Name"
              placeholder="Type brief description of the service"
              value={serviceDetails?.description || ""}
              handleChange={(e) =>
                setServiceDetails((prev: any) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              name="placeholderInput"
              maxLength={500}
              showCharacterCount={true}
              maxRows={8}
            />
          </Box>
          <Box>
            <Box sx={styles.title(theme)}>Upload photo</Box>
            <ImageUpload
              uploadedImage={uploadedImage}
              setUploadedImage={(img) => {
                setUploadedImage(img);
                // Persist to serviceDetails so the preview survives step navigation (unmount/remount)
                setServiceDetails((prev: any) => ({
                  ...prev,
                  imageURL: img,
                }));
              }}
              onFileSelect={(file) => {
                onImageSelect?.(file);
              }}
            />
          </Box>

          {serviceType !== "Lab work" &&
            serviceType !== "Medical procedures" && (
              <ServiceBenefitsWidget
                serviceDetails={serviceDetails}
                setServiceDetails={setServiceDetails}
              />
            )}
          {(serviceType === "IV Therapy" ||
            serviceType === "Pharmaceuticals" ||
            serviceType === "Pharmaceuticals IV" ||
            serviceType === "Supplements") && (
            <ActiveIngredientsWidget
              activeIngredients={serviceDetails?.activeIngredients || []}
              onChange={(ingredients) =>
                setServiceDetails((prev: any) => ({
                  ...prev,
                  activeIngredients: ingredients,
                }))
              }
              serviceType={serviceType}
              title={
                serviceType === "Supplements"
                  ? "Active Substance(s)/ Formula Type"
                  : serviceType === "Pharmaceuticals"
                  ? "Active Substance(s)"
                  : "Active Compound(s)"
              }
            />
          )}
          {serviceDetails?.attributes?.map((attribute: any, index: number) => (
            <AttributeRenderer
              key={`${attribute.attributeID}-${attribute.reportSvcAttributeID}-${index}`}
              attribute={attribute}
              onAttributeChange={(updatedAttribute: any) => {
                setServiceDetails((prevDetails: any) => {
                  const updatedAttributes = [...prevDetails.attributes].map(
                    (attr: any, attrIndex: number) => {
                      // Match by attributeID and index to avoid closure issues
                      if (
                        attr.attributeID === updatedAttribute.attributeID &&
                        attrIndex === index
                      ) {
                        return updatedAttribute;
                      }
                      return attr;
                    }
                  );
                  return {
                    ...prevDetails,
                    attributes: updatedAttributes,
                  };
                });
              }}
            />
          ))}
          {/* Dynamic fields based on service type */}
          {dynamicFields}
          <Box sx={{ height: "60px" }} />
        </Box>
      </ScrollableWrapper>
    </Box>
  );
};

// Standalone validation function for Step 2
export const validateStep2 = (
  serviceDetails: ServiceDetails,
  uploadedImage?: string | null,
  existingServices: ExistingService[] = [],
  excludeId: string | null = null
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // 1. Mandatory fields validation
  if (!serviceDetails.name || serviceDetails.name.trim() === "") {
    errors.push("Please enter a service name");
  } else if (
    isDuplicateName(serviceDetails.name, existingServices, excludeId)
  ) {
    errors.push(DUPLICATE_NAME_ERROR);
  }

  if (!serviceDetails.description || serviceDetails.description.trim() === "") {
    errors.push("Please enter a service description");
  }

  // Check uploadedImage parameter if provided, otherwise fall back to serviceDetails.imageURL
  const hasImage = uploadedImage
    ? uploadedImage.trim() !== ""
    : serviceDetails.imageURL && serviceDetails.imageURL.trim() !== "";
  if (!hasImage) {
    errors.push("Please upload a photo");
  }

  // 2. Service type specific validations
  const serviceType = serviceDetails.serviceType
    ? enReportServiceType[serviceDetails.serviceType]
    : null;

  // 3. Active Ingredients validation (only validate if ingredients were added)
  if (
    serviceType === "Supplements" ||
    serviceType === "IV Therapy" ||
    serviceType === "Pharmaceuticals" ||
    serviceType === "Pharmaceuticals IV"
  ) {
    const activeIngredients = serviceDetails.activeIngredients || [];

    // Only validate if user has started adding ingredients
    if (activeIngredients.length > 0) {
      // Check if all active ingredients are complete and valid
      let hasIncompleteIngredients = false;

      activeIngredients.forEach((ingredient) => {
        // Check if ingredient has valid values
        const hasValue = ingredient.value !== null && ingredient.value !== 0;
        const hasRange =
          (ingredient.min !== null && ingredient.min !== 0) ||
          (ingredient.max !== null && ingredient.max !== 0);

        // Basic completeness check
        if (!hasValue && !hasRange) {
          hasIncompleteIngredients = true;
        }

        // Check if unit is provided
        if (!ingredient.unit || ingredient.unit.trim() === "") {
          hasIncompleteIngredients = true;
        }

        // Validate single value (must be positive)
        if (ingredient.value !== null && ingredient.value < 0) {
          hasIncompleteIngredients = true;
        }

        // Validate min value (must be positive)
        if (ingredient.min !== null && ingredient.min < 0) {
          hasIncompleteIngredients = true;
        }

        // Validate max value (must be positive)
        if (ingredient.max !== null && ingredient.max < 0) {
          hasIncompleteIngredients = true;
        }

        // Validate min < max relationship
        if (
          ingredient.min !== null &&
          ingredient.max !== null &&
          ingredient.min > 0 &&
          ingredient.max > 0 &&
          ingredient.min >= ingredient.max
        ) {
          hasIncompleteIngredients = true;
        }
      });

      if (hasIncompleteIngredients) {
        errors.push(
          `Please complete all ${
            serviceType === "Supplements"
              ? "active substance(s)/formula type"
              : serviceType === "Pharmaceuticals"
              ? "active substance(s)"
              : "active compound(s)"
          } information`
        );
      }
    }
  }

  // 4. Attributes validation
  if (serviceDetails.attributes && serviceDetails.attributes.length > 0) {
    const incompleteAttributes: string[] = [];

    serviceDetails.attributes.forEach((attribute) => {
      let isIncomplete = false;

      switch (attribute.type) {
        case 2: // Text input
          if (!attribute.textValue || attribute.textValue.trim() === "") {
            isIncomplete = true;
          }
          break;

        case 3: // Numeric input (can be single or range)
          // Check if it's single or range mode
          const hasSingleValueType3 =
            attribute.value !== null &&
            attribute.value !== undefined &&
            attribute.value !== 0;
          const hasRangeValuesType3 =
            (attribute.minValue !== null &&
              attribute.minValue !== undefined &&
              attribute.minValue !== 0) ||
            (attribute.maxValue !== null &&
              attribute.maxValue !== undefined &&
              attribute.maxValue !== 0);

          if (!hasSingleValueType3 && !hasRangeValuesType3) {
            isIncomplete = true;
          }

          // Validate range constraints using the NumberAndRangeElement validation
          if (hasSingleValueType3 || hasRangeValuesType3) {
            const validationError = validateDurationAttribute(attribute);
            if (validationError) {
              // Add this specific error to the errors array
              errors.push(`${attribute.name}: ${validationError}`);
            }
          }
          break;

        case 6: // List selection
          if (!attribute.textValue || attribute.textValue.trim() === "") {
            isIncomplete = true;
          }
          break;

        case 1: // Range (label/min/max/unit)
        case 4: // Time (label/time/time unit)
        case 5: // Time range (label/time min/time max/time unit)
        case 7: // Period range (label/time min/time max/time unit)
          // Check if it's single or range mode
          const hasSingleValue =
            attribute.value !== null &&
            attribute.value !== undefined &&
            attribute.value !== 0;
          const hasRangeValues =
            (attribute.minValue !== null &&
              attribute.minValue !== undefined &&
              attribute.minValue !== 0) ||
            (attribute.maxValue !== null &&
              attribute.maxValue !== undefined &&
              attribute.maxValue !== 0);

          if (!hasSingleValue && !hasRangeValues) {
            isIncomplete = true;
          }

          // Check unit for time-related types (4, 5, 7)
          if ([4, 5, 7].includes(attribute.type)) {
            if (!attribute.unitValue || attribute.unitValue.trim() === "") {
              isIncomplete = true;
            }
          }

          // Validate range constraints using the NumberAndRangeElement validation
          if (attribute.type === 1 && (hasSingleValue || hasRangeValues)) {
            const validationError = validateDurationAttribute(attribute);
            if (validationError) {
              // Add this specific error to the errors array
              errors.push(`${attribute.name}: ${validationError}`);
            }
          }
          break;
      }

      if (isIncomplete) {
        incompleteAttributes.push(attribute.name);
      }
    });

    if (incompleteAttributes.length > 0) {
      if (incompleteAttributes.length === 1) {
        errors.push(`Please complete the "${incompleteAttributes[0]}" field`);
      } else {
        errors.push(
          `Please complete the following fields: ${incompleteAttributes.join(
            ", "
          )}`
        );
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default ServiceBuilderStep2;

const styles = {
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "32px",
    paddingBottom: "20px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    flex: 1,
    marginBottom: "16px",
  }),
  subtitle: () => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
};
